import { RESERVEDDatasourceCanInfoIcon } from '@tableau/icons/RESERVEDDatasourceCanInfoIcon';
import { RESERVEDDatasourceCanWarningIcon } from '@tableau/icons/RESERVEDDatasourceCanWarningIcon';
import { TabStyles, ButtonType } from '@tableau/widgets';
import { Formatters } from '@tableau/widgets/lib/gen-src/Localize';
import React from 'react';
import { Messages } from '../../../../../../gen-src/Localize';
import { assertUnreachable } from '../../utils/TypescriptHelpers';
import { WarningHighSeverityIcon, WarningStandardSeverityIcon } from '../LabelIcons';
import { LabelPopover } from '../LabelPopover';
import { LabelIconClass, LabelIconHoverClass } from '../Styles';
import { LabelRenderControlsWrapper } from '../shared/LabelRenderProps';
import { LabelType, localizeLabelTypeName, stringtoLabelType } from '../shared/LabelType';
import { useControlledPopover, ChipTriggerElementProps, ButtonTriggerElementProps } from '../shared/useControlledPopover';
import { WarningRenderProps } from './WarningProps';

export type WarningLabelProps = WarningRenderProps & LabelRenderControlsWrapper;

function warningLabelTitle(props: WarningLabelProps): string {
  switch (props.kind) {
    case 'dehydrated': {
      return warningLabelTitleCalculation(props.count, props.labelValue);
    }
    case 'hydrated': {
      const count = (props.anchored?.labels.length ?? 0) + (props.upstream?.labels.length ?? 0) + (props.parented?.labels.length ?? 0);
      const labelType = props.anchored?.labels[0]?.type ?? props.parented?.labels[0]?.type ?? props.upstream?.labels[0]?.type ?? '';

      return warningLabelTitleCalculation(count, labelType);
    }
    default:
      assertUnreachable(props)
      return '';
  }
}

function warningLabelTitleCalculation(count: number, labelType: string): string {
  if (count <= 0) {
    return '';
  }
  if (count === 1) {
    return labelType in LabelType ? localizeLabelTypeName(stringtoLabelType(labelType)) : labelType
  }
  return Messages.Label_quality_warning_chip_message({
    MESSAGE_COUNT: count,
    MESSAGE_COUNT_LOC: Formatters.formatCount(count),
  });
}

function warningIsSevere(props?: WarningRenderProps): boolean {
  if (!props) {
    return false;
  }
  switch (props?.kind) {
    case 'hydrated':
      if (props.anchored?.labels.some(w => w.severe) || props.parented?.labels.some(w => w.severe)) {
        return true;
      }
      break;
    case 'dehydrated':
      return props.severe;
    default:
      assertUnreachable(props);
  }

  return props?.upstream?.labels.some(w => w.severe) ?? false;
}

export function WarningLabel(props: WarningLabelProps): JSX.Element {
  const popover = useControlledPopover<WarningRenderProps>({
    testId: props.testId,
    popoverProps: {
      title: warningLabelTitle(props),
      placement: props.controls?.popoverPlacement ?? 'bottom-start',
    },
    content: LabelPopover,
    controlKind: props.controls?.kind ?? 'icon',
    iconProps: warningIconProps,
    chipProps: warningChipProps,
    onClick: props.onClick,
    loading: props.loading,
    props: {
      ...props
    },
    severe: warningIsSevere(props),
  })
  return popover ?? <></>;
}

function warningChipProps(severe: boolean): ChipTriggerElementProps {
  return {
    stickerType: severe ? 'yellow' : 'blue',
    icon: severe ? <RESERVEDDatasourceCanWarningIcon size={14} alt={Messages.DataQualityTypeWarning()} color={'#AC4B02'} /> : <RESERVEDDatasourceCanInfoIcon size={14} alt={Messages.DataQualityTypeWarning()} color={'#05628A'} />,
    kind: 'sticker-props'
  }
}

function warningIconProps(severe: boolean): ButtonTriggerElementProps {
  return {
    getContent: (s) => <WarningIcon hover={s.hover} isSevere={() => severe} />,
    style: {
      padding: TabStyles.Spacing.SmallSpace,
      cursor: 'pointer',
    },
    buttonType: ButtonType.Icon,
    kind: 'button-props',
  }
}

export function WarningIcon(props: { hover?: boolean, isSevere: () => boolean }): JSX.Element {
  const severe = props.isSevere();
  if (props.hover) {
    return severe ?
      <WarningHighSeverityIcon className={LabelIconHoverClass} /> :
      <WarningStandardSeverityIcon className={LabelIconHoverClass} />;
  } else {
    return severe ?
      <WarningHighSeverityIcon className={LabelIconClass} /> :
      <WarningStandardSeverityIcon className={LabelIconClass} />;
  }
}
